import React, { useMemo, useState } from 'react';
import { LuArrowUpDown, LuArrowUp, LuArrowDown } from 'react-icons/lu';

// columns: [{ key, label, render(row), sortValue?(row), align?, className? }]
const EngagementsTable = ({ columns, rows, defaultSort, emptyMessage = 'Nothing to show.', getRowKey }) => {
  const [sort, setSort] = useState(defaultSort || { key: columns[0]?.key, dir: 'asc' });

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const value = col.sortValue || ((r) => r[col.key]);
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, columns, sort]);

  const toggle = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  if (!rows.length) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="w-full overflow-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/60">
            {columns.map((col) => {
              const active = sort.key === col.key;
              const Icon = active ? (sort.dir === 'asc' ? LuArrowUp : LuArrowDown) : LuArrowUpDown;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={`h-9 whitespace-nowrap px-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(col.key)}
                    className={`inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground ${
                      active ? 'text-foreground' : ''
                    }`}
                  >
                    {col.label}
                    <Icon className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} aria-hidden="true" />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={getRowKey ? getRowKey(row, i) : i} className="border-b border-border transition-colors last:border-0 hover:bg-muted/40">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2.5 align-middle text-foreground ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${
                    col.className || ''
                  }`}
                >
                  {col.render ? col.render(row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EngagementsTable;
